import { useState, useEffect } from 'react';
import { LoginForm, RegisterForm, ForgotPasswordForm } from './';

type AuthMode = 'login' | 'register' | 'forgot-password';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoginSuccess: (user: any) => void;
  showToastMessage: (message: string) => void;
}

/**
 * AuthModal 组件
 * 像素风RPG主题认证模态框，整合登录、注册、忘记密码功能
 */
export function AuthModal({ isOpen, onClose, onLoginSuccess, showToastMessage }: AuthModalProps) {
  const [authMode, setAuthMode] = useState<AuthMode>('login');
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  // 处理打开/关闭动画
  useEffect(() => {
    if (isOpen) {
      setIsClosing(false);
      setIsVisible(true);
      return undefined;
    } else {
      setIsClosing(true);
      const timer = setTimeout(() => {
        setIsVisible(false);
        setIsClosing(false);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // ESC键关闭
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isVisible && !isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className={`fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 transition-all duration-200 ${
        isClosing ? 'opacity-0' : 'opacity-100'
      }`}
      onClick={handleBackdropClick}
    >
      {/* 背景遮罩 - 像素风暗色 */}
      <div className="absolute inset-0 bg-wood-darker/80 backdrop-blur-sm" />
      
      {/* 装饰性背景图案 */}
      <div className="absolute inset-0 opacity-5 pointer-events-none"
           style={{
             backgroundImage: `repeating-linear-gradient(
               0deg,
               transparent,
               transparent 2px,
               rgba(255,255,255,0.1) 2px,
               rgba(255,255,255,0.1) 4px
             )`,
           }} />

      {/* 主容器 - 像素风RPG弹窗 */}
      <div 
        className={`relative w-full max-w-[420px] transition-all duration-200 ${
          isClosing 
            ? 'opacity-0 scale-95 translate-y-2' 
            : 'opacity-100 scale-100 translate-y-0'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 像素阴影层 */}
        <div className="absolute inset-0 translate-x-1.5 translate-y-1.5 bg-wood-darker/40 jagged-corners pointer-events-none" />
        
        {/* 主内容容器 */}
        <div className="relative bg-parchment border-2 border-wood-darker jagged-corners overflow-hidden">
          {/* 顶部装饰条 */}
          <div className="h-2 bg-gradient-to-r from-wood-dark via-wood-medium to-wood-dark border-b-2 border-wood-darker" />
          
          {/* 内部羊皮纸纹理 */}
          <div className="absolute inset-0 opacity-30 pointer-events-none"
               style={{
                 backgroundImage: `repeating-linear-gradient(
                   90deg,
                   transparent,
                   transparent 1px,
                   rgba(139, 69, 19, 0.03) 1px,
                   rgba(139, 69, 19, 0.03) 2px
                 )`,
               }} />
          
          {/* 内容区域 */}
          <div className="relative p-5 sm:p-6">
            {authMode === 'login' && (
              <LoginForm
                onSuccess={(user) => {
                  onLoginSuccess(user);
                  onClose();
                  showToastMessage('登录成功！');
                }}
                onClose={onClose}
                onSwitchToRegister={() => setAuthMode('register')}
                onSwitchToForgotPassword={() => setAuthMode('forgot-password')}
                onError={(error) => showToastMessage(error)}
              />
            )}
            {authMode === 'register' && (
              <RegisterForm
                onSuccess={(user) => {
                  onLoginSuccess(user);
                  onClose();
                  showToastMessage('注册成功！');
                }}
                onClose={onClose}
                onSwitchToLogin={() => setAuthMode('login')}
                onError={(error) => showToastMessage(error)}
              />
            )}
            {authMode === 'forgot-password' && (
              <ForgotPasswordForm
                onSuccess={() => {
                  onClose();
                  showToastMessage('密码重置成功！请使用新密码登录。');
                  setAuthMode('login');
                }}
                onCancel={onClose}
                switchToLogin={() => setAuthMode('login')}
              />
            )}
          </div>
          
          {/* 底部装饰条 */}
          <div className="h-2 bg-gradient-to-r from-wood-dark via-wood-medium to-wood-dark border-t-2 border-wood-darker" />
        </div>

        {/* 角落装饰 - 像素方块 */}
        <div className="absolute -top-1 -left-1 w-3 h-3 bg-wood-medium border border-wood-darker" />
        <div className="absolute -top-1 -right-1 w-3 h-3 bg-wood-medium border border-wood-darker" />
        <div className="absolute -bottom-1 -left-1 w-3 h-3 bg-wood-medium border border-wood-darker" />
        <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-wood-medium border border-wood-darker" />
      </div>
    </div>
  );
}